import { z } from "zod";
import { JobStatusSchema } from "./machine-job";

/**
 * 생산 실적 스키마
 * 장비의 작업 기록과 연결된 주문 정보를 포함합니다.
 */
export const ProductionRecordSchema = z.object({
  id: z.number(),
  routerId: z.number().nullable(),
  routerName: z.string().nullable(),
  machineId: z.number(),
  machineName: z.string().nullable(),
  jobName: z.string().nullable(),
  startTime: z.string(),
  completeTime: z.string().nullable(),
  status: JobStatusSchema,
  orderId: z.number().nullable(),
  orderNumber: z.string().nullable(),
  orderName: z.string().nullable(),
});

export type ProductionRecord = z.infer<typeof ProductionRecordSchema>;

/**
 * 생산 실적 조회 요청 파라미터
 */
export interface GetProductionRecordsRequest {
  companyId: number;
  routerId?: number;
  machineId?: number;
  startDate?: string;
  endDate?: string;
  hasOrder?: boolean;
  page?: number;
  size?: number;
}

/**
 * 생산 실적 필터 옵션
 */
export interface ProductionRecordFilters {
  routerId?: number;
  machineId?: number;
  startDate?: Date | null;
  endDate?: Date | null;
  hasOrder?: boolean | null;
}

/**
 * 생산 실적 엔티티 클래스
 */
export class ProductionRecordEntity implements ProductionRecord {
  readonly id: number;
  readonly routerId: number | null;
  readonly routerName: string | null;
  readonly machineId: number;
  readonly machineName: string | null;
  readonly jobName: string | null;
  readonly startTime: string;
  readonly completeTime: string | null;
  readonly status: ProductionRecord["status"];
  readonly orderId: number | null;
  readonly orderNumber: string | null;
  readonly orderName: string | null;

  constructor(data: ProductionRecord) {
    const validated = ProductionRecordSchema.parse(data);

    this.id = validated.id;
    this.routerId = validated.routerId;
    this.routerName = validated.routerName;
    this.machineId = validated.machineId;
    this.machineName = validated.machineName;
    this.jobName = validated.jobName;
    this.startTime = validated.startTime;
    this.completeTime = validated.completeTime;
    this.status = validated.status;
    this.orderId = validated.orderId;
    this.orderNumber = validated.orderNumber;
    this.orderName = validated.orderName;
  }

  /**
   * 주문과 연결되어 있는지 확인
   */
  hasLinkedOrder(): boolean {
    return this.orderId !== null;
  }

  /**
   * 작업이 완료되었는지 확인
   */
  isCompleted(): boolean {
    return this.completeTime !== null;
  }

  /**
   * 작업 시간(분) 계산
   */
  getDurationInMinutes(): number | null {
    if (!this.completeTime) return null;

    const start = new Date(this.startTime);
    const end = new Date(this.completeTime);
    return Math.floor((end.getTime() - start.getTime()) / (1000 * 60));
  }

  /**
   * 표시용 작업명 반환
   */
  getDisplayJobName(): string {
    return this.jobName || `Job ${this.id}`;
  }

  /**
   * 상태 레이블 반환
   */
  getStatusLabel(): string {
    const statusLabels: Record<ProductionRecord["status"], string> = {
      PENDING: "대기중",
      PENDING_HELD: "대기 (보류)",
      PROCESSING: "진행중",
      PROCESSING_STOPPED: "진행 중단",
      CANCELED: "취소됨",
      ABORTED: "중단됨",
      COMPLETED: "완료",
    };
    return statusLabels[this.status] || this.status;
  }

  static fromApiResponse(data: unknown): ProductionRecordEntity {
    return new ProductionRecordEntity(data as ProductionRecord);
  }
}
